// What the operator panel prints in a queue row, as data.
//
// The wizard's Final Production Review has to read like `:5005/<machine>-hmi`,
// because that is the screen the planner is predicting. These are the panel's
// own three rules, kept here rather than inside the React component so they can
// be checked AGAINST the panel: tests/launchPreviewRender.test.js loads the real
// l120 page in a sandbox and compares its `formatCopperLine` with this one.
//
// Data, not markup — the component decides how a line looks, this decides what
// it says.

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Thousands the Romanian way; `—` when there is no number at all. */
export const fmtMeters = n =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString('ro-RO') : '—';

/** What the panel calls a drum that is going somewhere else next. */
const DELIVERY = {
  coils_on_machine: { text: 'COLACI PE MASINA', bg: 'info' },
  to_coiling: { text: 'PTR COLACI', bg: 'info' },
  to_rewinding: { text: 'PTR DERULARE', bg: 'warning' }
};

/**
 * The badge under the phase. Anything unrecognised is a FINAL PRODUCT, exactly
 * as on the panel — a drum with no onward route is finished goods.
 */
export function deliveryOf(mode) {
  return DELIVERY[String(mode || '').toLowerCase()] || { text: 'PRODUS FINAL', bg: 'success' };
}

/**
 * A diameter the way the panel writes one: a whole number keeps no decimals, so
 * a Ø2 wire reads "2" and not "2.00".
 */
export function fmtDiameter(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0.00';
  if (Math.abs(x - Math.round(x)) < 0.000001) return String(Math.round(x));
  return x.toFixed(2);
}

/**
 * One copper line, the way the panel writes it.
 *
 * Mirrors `formatCopperLine` in machine_hmi_interface/public/*-hmi.html branch
 * for branch, including the fallback texts: a SINGLE-WIRE conductor reads as its
 * diameter, a stranded one states how many wires — and with how many drums, when
 * the rope is built from several — because that is what tells an operator which
 * pay-off to load. tests/launchPreviewRender.test.js compares the two outputs.
 */
export function copperLineText(item) {
  const type = String(item?.wireType || '').toUpperCase();
  const d = item?.wireDiameter;
  const wires = num(item?.numberOfWires);
  const drums = num(item?.numberOfDrums);

  if (type === 'SINGLE-WIRE') return `Cu Ø ${fmtDiameter(d)} mm`;

  if (type === 'STRANDED') {
    if (drums && wires && d) return `${drums} x ${wires} x ${fmtDiameter(d)} mm`;
    if (wires && d) return `${wires} x ${fmtDiameter(d)} mm`;
    return 'Cupru stranded';
  }

  if (type === 'MULTI-WIRE') {
    if (wires && d) return `${wires} x ${fmtDiameter(d)} mm`;
    return 'Cupru multi-wire';
  }

  return 'Cupru';
}

/**
 * The Input column of one execution: its copper lines and its compound lines.
 *
 * @returns `[{ text, kind: 'copper' | 'compound', color }]`
 */
export function materialLines(task) {
  const out = [];
  for (const item of task?.materialPlan || []) {
    if (!item) continue;

    if (String(item.material || '').toUpperCase() === 'COPPER') {
      out.push({ text: copperLineText(item), kind: 'copper', color: null });
      continue;
    }

    // The panel falls back to the layer name when a compound has neither a
    // material nor a grade — better "exterior_sheat" than an empty cell.
    const text = [item.material, item.grade].filter(Boolean).join(' ') || item.layer || '';
    if (text) out.push({ text, kind: 'compound', color: item.grade || item.color || null });
  }
  return out;
}

/**
 * `1 x 13 300 m + 1 x 1 050 m` — a merged drum states both shares, because the
 * two numbers are what the operator has to cut.
 */
export function executionText(block) {
  return (block?.items || [])
    .map(item => {
      const t = item?.task || {};
      if (t.phase === 'coiling' && t.coilCount && t.coilLength) {
        return `${t.coilCount} x ${fmtMeters(t.coilLength)} m`;
      }
      if (t.phase === 'rewinding' && t.rewindCount && t.rewindLength) {
        return `${t.rewindCount} x ${fmtMeters(t.rewindLength)} m`;
      }
      if (t.deliveryMode === 'coils_on_machine' && t.coilCount && t.coilLength) {
        return `${t.coilCount} x ${fmtMeters(t.coilLength)} m`;
      }
      return `1 x ${fmtMeters(t.plannedLength)} m`;
    })
    .join(' + ');
}

/** The wire Ø an execution runs, when that is what its phase is about. */
export function wireDiameterOf(task) {
  const d = (task?.materialPlan || []).find(m => num(m?.wireDiameter))?.wireDiameter;
  return Number.isFinite(Number(d)) ? Number(d) : null;
}
